<template>
  <div class="checkbox-group">
    <div v-for="(option, index) in opts" :key="index" class="checkbox-group-option">
      <input :id="`${group_name}-${index}`"
             type="checkbox"
             :name="group_name"
             :value="option.value"
             :checked="option.checked"
             @input="(ev) => handleInput(ev, index)">
      <label :for="`${group_name}-${index}`"
             class="btn"
             :class="`${option.checked ? checkedVariant : uncheckedVariant}`">
        <i v-if="option.checked" class="icon la la-check-circle" />
        <i v-else class="icon la la-circle" />
        {{ option.display }}
      </label>
    </div>
  </div>
</template>

<script>
const getNextId = (function () {
  let id = 0;
  return () => id++;
})();

export default {
  props: {
    options: {
      type: Array,
      required: true,
    },
    id: {
      type: String,
      required: false,
      default: null,
    },
    flexDirection: {
      type: String,
      required: false,
      default: 'row',
    },
    checkedVariant: {
      type: String,
      required: false,
      default: 'checkbox-btn checked',
    },
    uncheckedVariant: {
      type: String,
      required: false,
      default: 'checkbox-btn',
    },
    value: {
      type: Array,
      required: false,
      default: () => ([]),
    },
  },
  data: () => ({
    opts: [],
  }),
  computed: {
    group_name() {
      return `checkbox-group-${this.id ?? getNextId()}`;
    },
    checked() {
      return this.opts.filter(opt => opt.checked);
    },
  },
  watch: {
    value: {
      deep: true,
      handler(new_value) {
        for (const opt of this.opts) {
          if (new_value.includes(opt.value)) {
            opt.checked = true;
          }
        }
      },
    },
  },
  beforeMount() {
    this.opts = [
      ...this.options,
    ].map(opt => ({
      ...opt,
      value: opt.value ?? opt.display,
      checked: this.value[opt.value ?? opt.display] ?? !!opt.checked,
    }));
  },
  methods: {
    handleInput(ev, opt_index) {
      this.opts.splice(opt_index, 1, { // have to reassign bc of reactivity
        ...this.opts[opt_index],
        checked: ev.target.checked,
      });
      const opt = this.opts[opt_index];
      this.$emit('input', this.checked.map(checked => checked.value));
      this.$emit('changed', {
        changed: opt,
        checked: this.checked,
        options: this.opts,
      });
    },
  },
};
</script>

<style lang="scss" scoped>
@use "sass:map";
@use "sass:color";

@use "~AssetsSass/_variables" as *;
@use "~AssetsSass/buttons";

.checkbox-group {
  display: flex;
  flex-wrap: wrap;

  .checkbox-btn {
    @extend %btn-ss-base;

    display: flex;
    flex-direction: flex-row;
    border: 1px solid map.get($ss-adds, 'light-grey');
    border-right-width: 0;
    border-radius: 0;
    width: 100%;
    height: 100%;
    margin: 0;

    &:hover {
      background-color: map.get($ss, 'primary');
      color: white !important;

      border-color: solid map.get($ss-adds, 'light-grey');
    }

    &.checked {
      font-weight: bold;
      font-size: 0.97em; // this avoids the layout being pushed around by bold text

      &:hover {
        color: white !important;
      }

      .icon {
        filter: none;
        color: color.adjust(map.get($ss-adds, 'confirm'), $lightness: -8%, $space: hsl);
      }
    }

    .icon {
      font-size: 1.25em;
      margin-right: -0.1em;
      filter: opacity(0.5);
    }
  }

  // here we re-add border rounding for the first and last children
  .checkbox-group-option:first-child {
    .btn {
      border-top-left-radius: .25rem;
      border-bottom-left-radius: .25rem;
    }
    .checkbox-btn {
      border-left-width: 1px;
    }
  }

  .checkbox-group-option:last-child {
    .btn {
      border-top-right-radius: .25rem;
      border-bottom-right-radius: .25rem;
    }
    .checkbox-btn {
      border-right-width: 1px;
    }
  }

  // default styling for the checkbox 'tabs'
  // they have `btn` classes also applied; see the markup
  .checkbox-group-option {
    input { // hide the actual input
      display: none;
    }
  }
}
</style>
